import { SupabaseClient } from '@supabase/supabase-js';
import { ChatbotTone, ToneSettings } from '../types/project-settings.types';

export interface ProjectSettings {
  id: string;
  project_id: string;
  chatbot_tone: ChatbotTone;
  tone_custom_prompt?: string;
  tone_settings: ToneSettings;
  welcome_message?: string;
  created_at: string;
  updated_at: string;
}

export interface UpdateProjectSettingsDTO {
  chatbot_tone?: ChatbotTone;
  tone_custom_prompt?: string;
  tone_settings?: ToneSettings;
  welcome_message?: string;
}

export class ProjectSettingsService {
  constructor(private supabase: SupabaseClient) {}

  /**
   * Get project settings
   */
  async getSettings(projectId: string): Promise<ProjectSettings | null> {
    const { data, error } = await this.supabase
      .from('project_settings')
      .select('*')
      .eq('project_id', projectId)
      .single();

    if (error) {
      if (error.code === 'PGRST116') {
        // No settings found, return null
        return null;
      }
      throw error;
    }

    return data;
  }

  /**
   * Create default project settings
   */
  async createDefaultSettings(projectId: string): Promise<ProjectSettings> {
    const { data, error } = await this.supabase
      .from('project_settings')
      .insert({
        project_id: projectId,
        chatbot_tone: 'professional',
        tone_settings: {
          use_emoji: false,
          formality_level: 'formal',
          response_length: 'moderate'
        },
        welcome_message: '안녕하세요! 무엇을 도와드릴까요?'
      })
      .select()
      .single();

    if (error) {
      throw error;
    }

    return data;
  }

  /**
   * Update project settings
   */
  async updateSettings(
    projectId: string,
    updates: UpdateProjectSettingsDTO
  ): Promise<ProjectSettings> {
    // First check if settings exist
    const existing = await this.getSettings(projectId);
    
    if (!existing) {
      // Create with updates
      const { data, error } = await this.supabase
        .from('project_settings')
        .insert({
          project_id: projectId,
          ...updates
        })
        .select()
        .single();

      if (error) {
        throw error;
      }

      return data;
    }

    // Update existing
    const { data, error } = await this.supabase
      .from('project_settings')
      .update(updates)
      .eq('project_id', projectId)
      .select()
      .single();

    if (error) {
      throw error;
    }

    return data;
  }

  /**
   * Update only tone settings
   */
  async updateToneSettings(
    projectId: string,
    tone: ChatbotTone,
    customPrompt?: string,
    settings?: ToneSettings
  ): Promise<ProjectSettings> {
    const updates: UpdateProjectSettingsDTO = {
      chatbot_tone: tone
    };

    if (tone === 'custom' && customPrompt) {
      updates.tone_custom_prompt = customPrompt;
    } else if (tone !== 'custom') {
      updates.tone_custom_prompt = null;
    }

    if (settings) {
      updates.tone_settings = settings;
    }

    return this.updateSettings(projectId, updates);
  }

  /**
   * Update only welcome message
   */
  async updateWelcomeMessage(
    projectId: string,
    welcomeMessage: string
  ): Promise<ProjectSettings> {
    return this.updateSettings(projectId, { welcome_message: welcomeMessage });
  }

  /**
   * Check if user can manage settings (owner or admin)
   */
  async canManageSettings(projectId: string, userId: string): Promise<boolean> {
    const { data } = await this.supabase
      .from('project_users')
      .select('role')
      .eq('project_id', projectId)
      .eq('user_id', userId)
      .single();

    return data?.role === 'owner' || data?.role === 'admin';
  }
}